import { Form } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  ExternalRedirectFormFields,
  ExternalRedirectFormInputs,
  ExternalRedirectsQueryKeys,
  useEditExternalRedirectsMutation,
  useGetOneByIdExternalRedirectsQuery,
} from '@/modules/ExternalRedirects';

import { ExternalRedirectsFormView } from './ExternalRedirectsFormView';
import { normalizeExternalRedirectPayload } from './normalize';
import { externalRedirectFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  externalRedirectId: string;
}

export const ExternalRedirectsEditFormContainer: FC<Props> = memo(({ siteId, externalRedirectId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ExternalRedirectFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ExternalRedirectFormInputs>>(null);
  const [targetSiteId, setTargetSiteId] = useState<string | undefined>(undefined);

  const {
    data: externalRedirectData,
    error: externalRedirectError,
    isLoading: isExternalRedirectLoading,
    isError: isExternalRedirectError,
    refetch: refetchExternalRedirect
  } = useGetOneByIdExternalRedirectsQuery(siteId, externalRedirectId)({
    enabled: !!externalRedirectId
  });

  // Підтягуємо сайт цільової сторінки з уже збереженого редіректу, інакше пікер "Сайт цілі"
  // лишається порожнім при редагуванні, хоча "До (сторінка)" вже має значення.
  useEffect(() => {
    if (targetSiteId === undefined && externalRedirectData?.targetNode?.siteId) {
      setTargetSiteId(externalRedirectData.targetNode.siteId);
    }
  }, [externalRedirectData?.targetNode?.siteId, targetSiteId]);

  const { mutate: editExternalRedirectMutation, isLoading: editExternalRedirectIsLoading } =
  useEditExternalRedirectsMutation(siteId, externalRedirectId)({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.GetAll]),
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.Conflicts]),
      ]);

      await refetchExternalRedirect();

      appMessageApi.success('Зовнішній редірект успішно змінено!');

      navigate(AppRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ExternalRedirectFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ExternalRedirectFormFields = {
    siteId,
    fromDomainId: externalRedirectData?.fromDomainId,
    sourceType: externalRedirectData?.sourceType,
    sourceNodeId: externalRedirectData?.sourceNodeId ?? undefined,
    sourcePath: externalRedirectData?.sourcePath ?? undefined,
    toType: externalRedirectData?.toType,
    toDomainId: externalRedirectData?.toDomainId ?? undefined,
    toExternalDomain: externalRedirectData?.toExternalDomain ?? undefined,
    toSiteId: externalRedirectData?.toSiteId ?? undefined,
    targetType: externalRedirectData?.targetType,
    targetNodeId: externalRedirectData?.targetNodeId ?? undefined,
    targetPath: externalRedirectData?.targetPath ?? undefined,
    statusCode: externalRedirectData?.statusCode,
    matchMode: externalRedirectData?.matchMode,
    forVisitor: externalRedirectData?.forVisitor,
    isActive: externalRedirectData?.isActive,
  };

  const handleFinish = useCallback((values: ExternalRedirectFormFields): void => {
    setFormErrors(null);
    editExternalRedirectMutation(normalizeExternalRedirectPayload(values));
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.ExternalRedirectsById.replace(':siteId', siteId).replace(':externalRedirectId', externalRedirectId));
  }, [siteId, externalRedirectId]);

  return (
    <FallbackedLayout
      error={ externalRedirectError }
      isError={ isExternalRedirectError }
      isLoading={ isExternalRedirectLoading }
    >
      {
        () => (
          <ExternalRedirectsFormView
            formName="external-redirect-edit"
            formType="edit"
            form={ form }
            formErrors={ formErrors }
            initialValues={ initialValues }
            isFormLoading={ editExternalRedirectIsLoading }
            validationSchema={ externalRedirectFormValidationSchema }
            siteId={ siteId }
            targetSiteId={ targetSiteId }
            handleChangeTargetSiteId={ setTargetSiteId }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
